import { create } from 'zustand';
import type { Tab, TabScreen, TabStackEntry } from '../types/domain';

const TAB_ROOT_SCREENS: Record<Tab, TabScreen> = {
  home: 'home',
  athletes: 'athlete-list',
  groups: 'group-list',
  profile: 'profile',
};

interface TabNavigationState {
  activeTab: Tab;
  tabStacks: Record<Tab, TabStackEntry[]>;

  // Bottom sheet states
  isHubOpen: boolean;
  isOrgSwitcherOpen: boolean;
  isDirectionSwitcherOpen: boolean;

  // Actions
  switchTab: (tab: Tab) => void;
  pushScreen: (screen: TabScreen, params?: Record<string, unknown>) => void;
  popScreen: () => boolean; // returns false if already at root
  resetTabStack: (tab: Tab) => void;
  getCurrentScreen: () => TabStackEntry;
  getStackDepth: () => number;

  // Bottom sheet actions
  openHub: () => void;
  closeHub: () => void;
  openOrgSwitcher: () => void;
  closeOrgSwitcher: () => void;
  openDirectionSwitcher: () => void;
  closeDirectionSwitcher: () => void;
}

export const useTabNavigationStore = create<TabNavigationState>((set, get) => ({
  activeTab: 'home',
  tabStacks: {
    home: [{ screen: 'home', params: {} }],
    athletes: [{ screen: 'athlete-list', params: {} }],
    groups: [{ screen: 'group-list', params: {} }],
    profile: [{ screen: 'profile', params: {} }],
  },

  // Bottom sheet states
  isHubOpen: false,
  isOrgSwitcherOpen: false,
  isDirectionSwitcherOpen: false,

  // Actions
  switchTab: (tab: Tab) => {
    set({ activeTab: tab });
  },

  pushScreen: (screen: TabScreen, params: Record<string, unknown> = {}) => {
    const { activeTab, tabStacks } = get();
    const currentStack = tabStacks[activeTab];

    set({
      tabStacks: {
        ...tabStacks,
        [activeTab]: [...currentStack, { screen, params }],
      },
    });
  },

  popScreen: () => {
    const { activeTab, tabStacks } = get();
    const currentStack = tabStacks[activeTab];

    // If already at root, cannot pop
    if (currentStack.length <= 1) {
      return false;
    }

    set({
      tabStacks: {
        ...tabStacks,
        [activeTab]: currentStack.slice(0, -1),
      },
    });

    return true;
  },

  resetTabStack: (tab: Tab) => {
    const { tabStacks } = get();
    const rootScreen = TAB_ROOT_SCREENS[tab];

    set({
      tabStacks: {
        ...tabStacks,
        [tab]: [{ screen: rootScreen, params: {} }],
      },
    });
  },

  getCurrentScreen: () => {
    const { activeTab, tabStacks } = get();
    const currentStack = tabStacks[activeTab];
    return currentStack[currentStack.length - 1];
  },

  getStackDepth: () => {
    const { activeTab, tabStacks } = get();
    return tabStacks[activeTab].length;
  },

  // Bottom sheet actions
  openHub: () => set({ isHubOpen: true }),
  closeHub: () => set({ isHubOpen: false }),
  openOrgSwitcher: () => set({ isOrgSwitcherOpen: true }),
  closeOrgSwitcher: () => set({ isOrgSwitcherOpen: false }),
  openDirectionSwitcher: () => set({ isDirectionSwitcherOpen: true }),
  closeDirectionSwitcher: () => set({ isDirectionSwitcherOpen: false }),
}));
